/** /health - inferred health observations grouped by kind. Each card
 *  is dotted until confirmed (inferred-vs-confirmed). */
import { useCallback, useEffect, useState } from "react";
import { HeartPulse, Check, X } from "lucide-react";
import { api } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";

type Entry = Record<string, unknown>;

const KIND_LABEL: Record<string, string> = {
  symptom: "Symptoms",
  medication: "Medications",
  vital: "Vitals",
  sleep: "Sleep",
  exercise: "Exercise",
  nutrition: "Nutrition",
  mood: "Mood",
  measurement: "Measurements",
  appointment: "Appointments",
  other: "Other",
};

export function Health() {
  const [items, setItems] = useState<Entry[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api.healthEntriesList().then((d) => setItems(d.entries || [])).catch(() => undefined);
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const act = async (id: string, action: "confirm" | "reject") => {
    setBusy(id);
    try {
      await api.healthTimeAction("health-entries", id, action);
      setItems((c) => c.filter((p) => p.id !== id || action === "confirm"));
      if (action === "confirm") refresh();
    } finally {
      setBusy(null);
    }
  };

  const byKind = items.reduce<Record<string, Entry[]>>((a, e) => {
    const k = String(e.kind || "other");
    (a[k] ||= []).push(e);
    return a;
  }, {});

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Health</h1>
      <TeachingBanner id="health_overview" />
      {Object.keys(byKind).length === 0 && (
        <p className="text-sm text-slate-500">Nothing logged yet.</p>
      )}
      {Object.entries(byKind).map(([kind, list]) => (
        <section key={kind} className="space-y-2">
          <h2 className="text-xs uppercase tracking-wider font-bold text-slate-500">
            {KIND_LABEL[kind] || kind}
          </h2>
          <ul className="space-y-2">
            {list.map((e) => {
              const id = String(e.id);
              const confirmed = Number(e.confirmed) === 1;
              const val = [e.value_num, e.unit].filter(Boolean).join(" ");
              return (
                <li
                  key={id}
                  className={`rounded-xl p-3 flex items-center justify-between gap-3 border ${
                    confirmed
                      ? "border-slate-200/70 dark:border-slate-700/50"
                      : "border-dashed border-slate-400 dark:border-slate-600"
                  } bg-white/60 dark:bg-slate-900/40`}
                >
                  <div className="min-w-0 flex items-center gap-2">
                    <HeartPulse className="w-4 h-4 text-rose-500" />
                    <span className="text-sm text-slate-900 dark:text-slate-100">{String(e.label)}</span>
                    {val && <span className="text-[12px] text-slate-500">{val}</span>}
                    {e.value_text ? (
                      <span className="text-[11px] text-slate-400 truncate">{String(e.value_text)}</span>
                    ) : null}
                  </div>
                  {!confirmed && (
                    <div className="flex gap-1.5 shrink-0">
                      <button type="button" disabled={busy === id} onClick={() => act(id, "confirm")} className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"><Check className="w-3.5 h-3.5" /></button>
                      <button type="button" disabled={busy === id} onClick={() => act(id, "reject")} className="inline-flex items-center gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
